/**
 * Признак кабинета «Показ гидрологии» на витрине.
 *
 * Часть кабинетов покупает ОЦЕНКУ, а не измерения, из которых она сложена
 * (`src/cabinet/org_features.py`). Сервер при выключенном признаке вырезает
 * среду из каталога слоёв и отвечает отказом на её ручки; здесь — то, что видно
 * глазами: чем открывается карта, какие наложения рисуются, какие инструменты
 * предлагаются.
 *
 * Разделы «Дрейфы» и «Апвеллинг» признаку НЕ подчиняются (решение владельца
 * 2026-08-17): снос читается только поверх поля течений, а индекс без своего
 * поля — пустая карта, то есть кнопка вместо раздела.
 *
 * ЭТО НЕ ГЕЙТ. Гейт — на сервере: витрина без чипа слоя остаётся витриной, а
 * запрос той же ручки из консоли — нет. Здесь мы лишь не показываем пальцем на
 * то, чего кабинету не отдадут, — иначе он видел бы чипы, каждый из которых
 * отвечает ошибкой.
 *
 * Каталог — SSOT прав: разрешено ровно то, что в нём приехало. Отдельным полем
 * едет только сам признак, потому что по составу списка его не вычислить
 * (`allowed_layers` режет тот же список по другой причине), а прятать надо ещё
 * и инструменты, своего слоя не имеющие вовсе.
 */
import type { LayerCatalog, LayerCatalogItem } from './api'

/**
 * Открыта ли среда этому кабинету.
 *
 * Каталог ещё не приехал — считаем открытой: иначе инструменты мигали бы
 * скрытием на каждой загрузке страницы у всех, кому среда как раз видна.
 * Ничем не рискуем — отказ всё равно даёт сервер.
 */
export function hydrologyOf(data: LayerCatalog | undefined): boolean {
  return data?.hydrology !== false
}

/**
 * Базовый слой, который кабинету разрешено показать.
 *
 * Ссылка приходит извне — из закладки, из «Поделиться», из чужого письма, — и
 * `layer=sst` в кабинете без среды не откроет ничего. Подставляем разрешённый,
 * а не оставляем пустую карту.
 *
 * Пустой каталог (ещё едет) выбор НЕ трогает: подмена в этот момент переписала
 * бы адрес и потеряла то, что человек открыл.
 */
export function clampLayer(active: string, layers: LayerCatalogItem[]): string {
  if (layers.length === 0) return active
  const base = layers.filter(l => !l.overlay && l.kind === 'raw')
  if (base.some(l => l.id === active)) return active
  return base[0]?.id ?? active
}

/**
 * Наложения, которые разрешено рисовать.
 *
 * Лёд, туман и облачность — среда, и в закрытом кабинете их нет в каталоге.
 * Чистить набор надо ОТДЕЛЬНО от переключателей: тумблеры строятся по каталогу
 * и лишнего не покажут, а рисует карта по набору из адреса (`overlays=…,ice`),
 * и без чистки лёд лёг бы на карту, которой его не выдавали.
 *
 * Набор без изменений возвращается тем же объектом: он ходит в зависимостях
 * эффектов, и новый массив каждый раз означал бы перерисовку на каждый кадр.
 */
export function clampOverlays(overlays: string[], layers: LayerCatalogItem[]): string[] {
  if (layers.length === 0) return overlays
  const allowed = new Set(layers.map(l => l.id))
  const kept = overlays.filter(id => allowed.has(id))
  return kept.length === overlays.length ? overlays : kept
}

// Модель раздела «Модели» признаку НЕ подчиняется: «Дрейфы» и «Апвеллинг»
// остаются при любой настройке (решение владельца 2026-08-17), поэтому
// приведения выбранной модели здесь нет и быть не должно. Их поля выведены из
// набора гидрологии у самих слоёв — см. `LayerSpec.hydrology`.
